/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import React, { lazy, Suspense } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import AvatarGroup from '@atlaskit/avatar-group/avatar-group';
import { jsx } from '@atlaskit/css';
import Link from '@atlaskit/link/link';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

import type {
	InvitedAppShortcutAllowedApsKey,
	InvitedAppShortcutCollaborator,
} from '../../../../../common/types';
import { messages } from '../../../../../common/ui/invited-app-shortcuts/messages';
import { getAppKeyByApsKey } from '../../../../../common/utils/invited-app-shortcuts-utils';
import { useInvitedAppAnalytics } from '../../../../../services/invited-app-shortcuts/invited-app-analytics';
import { useInvitedAppCollaboratorsLazy } from '../../../../../services/invited-app-shortcuts/invited-app-shortcuts-collaborators';
import { InvitedAppCollaboratorsTooltip } from './invited-app-collaborators-tooltip';
import type { CollaboratorsSource } from './types';

// Lazy load the InvitedAppPopup component
const InvitedAppPopup = lazy(() =>
	import(/* webpackChunkName: "@atlassian/invited-app-popup" */ './invited-app-popup').then(
		(module) => ({
			default: module.InvitedAppPopup,
		}),
	),
);

export interface LazyInvitedAppPopupProps {
	/**
	 * Source of collaborator avatars. Legacy/experiment callers pass
	 * `{ kind: 'static', collaborators }`; productionisation callers pass
	 * `{ kind: 'lazy', app }`. The popup is mode-agnostic.
	 */
	collaboratorsSource: CollaboratorsSource;
	currentApsKey: InvitedAppShortcutAllowedApsKey;
	currentShortcutLink: string;
	onMouseEnterPopup: () => void;
	onMouseLeavePopup: () => void;
	trigger: React.MutableRefObject<HTMLAnchorElement | null>;
}

// Loading skeleton dimensions, matching the "No collaborators (Skeleton)"
// Figma spec: two stacked lines of equal height and different widths that
// stand in for the popup's title + subtitle text while collaborators load.
const SKELETON_LINE_HEIGHT = '12px';
const SKELETON_TITLE_WIDTH = '180px';
const SKELETON_SUBTITLE_WIDTH = '120px';

/**
 * Renders the avatar group + tooltip for the given collaborators.
 * Returns `null` when there are no collaborators to show (error / idle / empty
 * all collapse to this branch).
 */
const PopupCollaboratorsBody = ({
	collaborators,
	currentApsKey,
}: {
	collaborators: InvitedAppShortcutCollaborator[];
	currentApsKey: InvitedAppShortcutAllowedApsKey;
}): JSX.Element | null => {
	if (collaborators.length === 0) {
		return null;
	}

	return (
		<Inline grow="hug">
			<AvatarGroup
				data={collaborators.map((collaborator) => ({
					...collaborator,
					appearance: 'circle',
					size: 'medium',
				}))}
				maxCount={2}
				overrides={{
					MoreIndicator: {
						render: (Component, props) => (
							<InvitedAppCollaboratorsTooltip
								currentApsKey={currentApsKey}
								currentAppCollaborators={collaborators}
							>
								<Component {...props} isActive={false} onClick={() => {}} />
							</InvitedAppCollaboratorsTooltip>
						),
					},
				}}
			/>
		</Inline>
	);
};

/**
 * Resolves the collaborator slice + loading flag for a given
 * `CollaboratorsSource`.
 * - `kind: 'static'` — synchronous read from the prop; never loading.
 * - `kind: 'lazy'`   — reads `useInvitedAppCollaboratorsLazy()` and slices
 *   by `app`. `isLoading` is true for every not-yet-resolved state (`idle`
 *   AND `loading`) so the popup shows the skeleton from the first frame
 *   rather than flashing the empty-state text before the fetch starts.
 *   `error` / missing-slice resolve to `[]` with `isLoading: false`.
 */
const useResolveCollaborators = (
	collaboratorsSource: CollaboratorsSource,
): {
	collaborators: InvitedAppShortcutCollaborator[];
	isLoading: boolean;
} => {
	// Called unconditionally to keep hook order stable across variants. In
	// experiment mode the provider is not mounted, so this reads the default
	// `{ status: 'idle' }` — no re-renders since its identity is fixed at
	// module load.
	const lazyState = useInvitedAppCollaboratorsLazy();

	if (collaboratorsSource.kind === 'static') {
		return { collaborators: collaboratorsSource.collaborators, isLoading: false };
	}
	// Not yet resolved (before the fetch starts, or while it's in flight) —
	// treat both as loading so the popup opens straight into the skeleton.
	if (lazyState.status === 'idle' || lazyState.status === 'loading') {
		return { collaborators: [], isLoading: true };
	}
	if (lazyState.status !== 'ready') {
		return { collaborators: [], isLoading: false };
	}
	return {
		collaborators: lazyState.collaboratorsByApp[collaboratorsSource.app] ?? [],
		isLoading: false,
	};
};

/** Resolves collaborators + count + `hasCollaborators` + `isLoading` for the popup. */
const useCollaboratorsInfo = (
	collaboratorsSource: CollaboratorsSource,
): {
	collaborators: InvitedAppShortcutCollaborator[];
	collaboratorsCount: number;
	hasCollaborators: boolean;
	isLoading: boolean;
} => {
	const { collaborators, isLoading } = useResolveCollaborators(collaboratorsSource);

	return {
		collaborators,
		collaboratorsCount: collaborators.length,
		hasCollaborators: collaborators.length > 0,
		isLoading,
	};
};

export const LazyInvitedAppPopup = ({
	collaboratorsSource,
	currentApsKey,
	currentShortcutLink,
	onMouseEnterPopup,
	onMouseLeavePopup,
	trigger,
}: LazyInvitedAppPopupProps): JSX.Element => {
	const intl = useIntl();
	const { fireLinkClickedAcceptInvite } = useInvitedAppAnalytics();

	const { collaborators, collaboratorsCount, hasCollaborators, isLoading } =
		useCollaboratorsInfo(collaboratorsSource);

	return (
		<Suspense fallback={null}>
			<InvitedAppPopup
				ariaLabel={intl.formatMessage(isLoading ? messages.popupLoadingLabel : messages.popupLabel)}
				trigger={trigger}
				onMouseEnterPopup={onMouseEnterPopup}
				onMouseLeavePopup={onMouseLeavePopup}
				currentApsKey={currentApsKey}
				hasCollaborators={hasCollaborators}
				collaboratorsCount={collaboratorsCount}
			>
				{isLoading ? (
					// While the lazy collaborators fetch is in flight, show only the
					// loading skeleton — two stacked lines standing in for the title +
					// subtitle text, no descriptive text or accept-invite link.
					<Stack space="space.100" testId="invited-app-collaborators-loading">
						<Skeleton
							width={SKELETON_TITLE_WIDTH}
							height={SKELETON_LINE_HEIGHT}
							borderRadius={token('radius.small')}
							isShimmering
						/>
						<Skeleton
							width={SKELETON_SUBTITLE_WIDTH}
							height={SKELETON_LINE_HEIGHT}
							borderRadius={token('radius.small')}
							isShimmering
						/>
					</Stack>
				) : (
					<Inline>
						<Inline grow="fill">
							<Stack alignBlock="start" alignInline="start">
								<Text>
									{hasCollaborators ? (
										<FormattedMessage {...messages.popupMainTextWithCollaborators} />
									) : (
										<FormattedMessage {...messages.popupMainTextWithoutCollaborators} />
									)}
								</Text>
								<Link
									href={currentShortcutLink}
									target="_blank"
									rel="noopener noreferrer"
									onClick={() => {
										fireLinkClickedAcceptInvite({ app: getAppKeyByApsKey(currentApsKey) });
									}}
								>
									<FormattedMessage {...messages.popupAcceptInviteButton} />
								</Link>
							</Stack>
						</Inline>
						<PopupCollaboratorsBody collaborators={collaborators} currentApsKey={currentApsKey} />
					</Inline>
				)}
			</InvitedAppPopup>
		</Suspense>
	);
};
